import axios from 'axios';
import {
  setHtmlScrollable,
  setHtmlUnscrollable
} from '@jspartials/general-utils';
import {
  classes,
  addClassToElement,
  hasClassElement,
  removeClassToElement
} from '@jspartials/class-list-utils';

import { listenDOMEvent, createElement } from '@jspartials/dom-api-utils';

const ModalTypes = {
  Static: Symbol('static'),
  Dynamic: Symbol('dynamic')
};

const selector = {
  classes: {
    js: 'js-modal',
    css: 'hw-modal',
    zIndex: 'tw-z-999'
  },
  modalTriggerClose: '.js-modal-trigger-close',
  modalContent: '.js-modal__content'
};

const SIZES = {
  sm: 'tw-max-w-sm',
  md: 'tw-max-w-md',
  lg: 'tw-max-w-lg',
  xl: 'tw-max-w-xl',
  '2xl': 'tw-max-w-2xl',
  '3xl': 'tw-max-w-3xl'
};

export default class Modal {
  static listOfModals = [];
  constructor(id, options) {
    let defaults = {
      state: 'closed',
      element: null,
      content: null,
      size: SIZES['sm'],
      type: ModalTypes.Static,
      parameters: null
    };
    this.id = id;
    this.options = Object.assign(defaults, options);

    const modalContentTemplate = `
    <div class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-full tw-h-full tw-p-4 tw-text-center">
      <div data-target="#modal" class="tw-fixed tw-inset-0 tw-block tw-transition-opacity tw-cursor-default js-modal-trigger-close focus:tw-hidden tw-bg-black/50">
        <span class="tw-sr-only">Close Modal</span>
      </div>

      <button data-target="#modal" class="tw-z-50 tw-p-2 tw-transition-opacity tw-border tw-border-gray-300 tw-rounded-full js-modal-trigger-close tw-bg-text-tertiary focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-black/50 focus:tw-ring-brand-accent focus:tw-ring-offset-2" type="button">
        <span class="tw-flex tw-justify-center tw-w-4 tw-h-4">
          <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          >
            <path clip-rule="evenodd" fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"></path>
          </svg>
        </span>
      </button>

      <div class="tw-w-full ${
        SIZES[this.options.size]
      } tw-my-8 tw-overflow-auto tw-text-left tw-transition-all tw-duration-300 tw-transform tw-scale-90 tw-shadow-xl tw-opacity-0 tw-bg-text-tertiary js-modal__content hw-modal__content">
        <span class="tw-min-h-[300px] tw-flex tw-justify-center tw-items-center">
          <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          class="tw-w-5 tw-h-5 tw-animate-spin"
          >
            <circle class="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        </span>
      </div>
    </div>
    `;

    this.options.element = createElement({
      tag: 'div',
      content: modalContentTemplate,
      attributes: [
        {
          name: 'id',
          value: this.id
        }
      ],
      classes: [
        selector.classes.js,
        selector.classes.css,
        selector.classes.zIndex
      ]
    });

    if (this.options.content !== null) {
      this.content = this.options.content;
    }

    document.body.appendChild(this.options.element);

    this._eventHandlers();
  }

  open() {
    this.options.state = 'open';
    var modal = this.options.element;
    addClassToElement(modal, classes.is.visible);
    setHtmlUnscrollable();
    const alreadyLoadedModal = hasClassElement(
      document.getElementById(this.id),
      classes.is.loaded
    );

    if (this.options.type !== ModalTypes.Static && !alreadyLoadedModal) {
      getModalContent(this.options.parameters)
        .then((data) => {
          setHtmlUnscrollable();

          this.content = data;
          addClassToElement(
            document.getElementById(this.id),
            classes.is.loaded
          );
        })
        .catch((error) => {
          console.log(error);
        });
    }

    document.dispatchEvent(
      new CustomEvent('modal-opened', { detail: this.id })
    );
  }

  close() {
    this.options.state = 'closed';
    setHtmlScrollable();
    var modal = this.options.element;
    removeClassToElement(modal, classes.is.visible);

    if (this.options.onClose) {
      this.options.onClose(this);
    }
  }

  set listOfModals(value) {
    this.listOfModals = value;
  }

  get state() {
    return this.options.state;
  }

  get content() {
    return this.options.element.querySelector(selector.modalContent).innerHTML;
  }

  get contentElement() {
    return this.options.element.querySelector(selector.modalContent);
  }

  set content(value) {
    if (!value) {
      this.options.element.remove();
    } else {
      this.options.element.querySelector(selector.modalContent).innerHTML =
        value;
    }
  }

  _eventHandlers() {
    this.options.element
      .querySelectorAll(selector.modalTriggerClose)
      .forEach((element) => {
        element.onclick = (event) => {
          event.preventDefault();
          this.close();
        };
      });
  }

  static initElements(context) {
    let auxArray = [];
    deletePreviousModals(context);
    document.querySelectorAll('[data-modal]').forEach((element) => {
      let modal;
      let modalID = element?.dataset?.modal || null;
      let modalOpenWithClick = element?.dataset?.modalOpenWithClick
        ? JSON.parse(element?.dataset?.modalOpenWithClick)
        : true;
      let rawModalElement = document.querySelector(
        `.${modalID}[data-modal-context="${context}"]`
      );
      let insertedModal = document.getElementById(modalID);

      // Static modal
      if (rawModalElement) {
        modal = new Modal(modalID, {
          content: rawModalElement.innerHTML,
          type: ModalTypes.Static,
          size: element.dataset.modalSize ? element.dataset.modalSize : 'sm'
        });
        // Removes base content for the modal
        rawModalElement.remove();
      } // Dynamic modal
      else if (!insertedModal && element.dataset.modalParameters) {
        modal = new Modal(modalID, {
          parameters: element.dataset.modalParameters,
          type: ModalTypes.Dynamic,
          size: element.dataset.modalSize ? element.dataset.modalSize : 'sm'
        });
      }

      if (modal) {
        if (modalOpenWithClick) {
          listenDOMEvent(element, 'click', (event) => {
            event.preventDefault();
            modal.open();
          });
        }

        auxArray.push(modal);

        document.dispatchEvent(
          new CustomEvent('modal-initialized', { detail: modalID })
        );
      }
    });
    this.listOfModals = auxArray;
    return this.listOfModals;
  }
}

const deletePreviousModals = (context) => {
  document
    .querySelectorAll(`[data-modal-context="${context}"]`)
    .forEach((element) => {
      let modalID = element.dataset.modal;

      let rawModalElement = document.querySelector(`#${modalID}`);
      if (rawModalElement) {
        rawModalElement.remove();
      }
    });
};

const getModalContent = (parameters) => {
  const formData = new FormData();
  formData.append('parameters', parameters);
  return new Promise((resolve, reject) => {
    axios
      .post(globalVariables.endpoint.getModal, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then(function (response) {
        if (response.status === 200) {
          resolve(response.data.htmlText);
        }
      })
      .catch(function (error) {
        reject(error);
      });
  });
};
